/**
 * @fileoverview Connect retained trash records to explicit restore, permanent deletion, and available undo. Shared metadata queries supply current state; backend governance owns retention/legal-hold enforcement. Selection and confirmation are local intent rather than proof that deletion is permitted. `kc-internal-backend/functions/simul_archive_trash.py` owns canonical trash behavior and operation results determine subsequent refresh.
 */

import useDocumentData from '../../store/Documents/useDocumentData'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api'
import AppIcon from '../shared/AppIcon'
import DocumentFileTypeIcon from './DocumentFileTypeIcon'
import './TrashView.css'

const MAX_TRASH_ITEMS = 500
const PERMANENT_CONFIRMATION = 'PERMANENTLY DELETE'



function errorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback
}



function itemKey(item) {
  return `${item?.item_type || ''}:${item?.item_id || ''}`
}



function itemRequest(item) {
  return { item_type: item.item_type, item_id: item.item_id }
}



function timestamp(value) {
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value < 100000000000 ? value * 1000 : value)
  const numeric = Number(value)
  if (value && Number.isFinite(numeric)) return new Date(numeric < 100000000000 ? numeric * 1000 : numeric)
  return value ? new Date(value) : null
}



export function formatTrashDate(value) {
  const date = timestamp(value)
  if (!date || Number.isNaN(date.getTime())) return 'Unknown date'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date)
}



export function originalLocationLabel(item) {
  const location = item?.original_location || {}
  const matter = String(location.matter_number || '').trim()
  const path = String(location.folder_path || location.parent_folder_path || '').trim()
  return [matter, path].filter(Boolean).join(' / ') || 'Documents root'
}



function retentionLabel(value, now = Date.now()) {
  const date = timestamp(value)
  if (!date || Number.isNaN(date.getTime())) return 'Retention date unavailable'
  return date.getTime() <= now ? 'Eligible for permanent deletion' : `Retained until ${formatTrashDate(date)}`
}



function operationFailures(operation, items) {
  const names = new Map(items.map(item => [itemKey(item), item.name || item.item_id]))
  return (operation?.results || [])
    .filter(result => result.status === 'failed')
    .map(result => ({
      key: `${result.item_type}:${result.item_id}`,
      name: names.get(`${result.item_type}:${result.item_id}`) || result.item_id,
      error: result.error || 'The item could not be changed',
    }))
}



function TrashConfirmation({ count, emptying, hasRetainedItems, busy, onCancel, onConfirm }) {
  const [confirmation, setConfirmation] = useState('')
  const [overrideRetention, setOverrideRetention] = useState(false)
  const [reason, setReason] = useState('')
  const inputRef = useRef(null)
  const returnFocusRef = useRef(null)
  const busyRef = useRef(busy)
  busyRef.current = busy

  useEffect(

  () => {
    returnFocusRef.current = document.activeElement
    inputRef.current?.focus()
    const onKeyDown =

    event => {
      if (event.key === 'Escape' && !busyRef.current) onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      returnFocusRef.current?.focus?.()
    }
  }, [onCancel])

  const ready = confirmation === PERMANENT_CONFIRMATION && (!overrideRetention || reason.trim())

  return (
    <div className="trash-confirmation-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onCancel()
    }}>
      <section className="trash-confirmation" role="dialog" aria-modal="true" aria-labelledby="trash-confirmation-title">
        <div className="trash-confirmation-signifier" aria-hidden="true">
          <AppIcon icon="pi pi-exclamation-triangle" />
        </div>
        <h3 id="trash-confirmation-title">{emptying ? 'Empty trash?' : `Delete ${count} item${count === 1 ? '' : 's'} permanently?`}</h3>
        <p>This removes archived versions, previews, comparisons, and indexed content. It cannot be undone.</p>
        {hasRetainedItems && (
          <label className="trash-retention-override">
            <input
              type="checkbox"
              checked={overrideRetention}
              onChange={event => setOverrideRetention(event.target.checked)} />
            Delete items whose retention period has not ended
          </label>
        )}
        {overrideRetention && (
          <label className="trash-confirmation-field">
            Reason for retention override
            <textarea value={reason} onChange={event => setReason(event.target.value)} rows="2" />
          </label>
        )}
        <label className="trash-confirmation-field">
          Type <strong>{PERMANENT_CONFIRMATION}</strong> to continue
          <input
            ref={inputRef}
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            autoComplete="off" />
        </label>
        <div className="trash-confirmation-actions">
          <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="is-danger"
            disabled={busy || !ready}
            onClick={() => onConfirm({ overrideRetention, reason: reason.trim() })}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </section>
    </div>
  )
}



export default function TrashView({
  canPermanentlyDelete = false,
  onChanged,
  onClose,
}) {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [failures, setFailures] = useState([])
  const [selected, setSelected] = useState(() => new Set())
  const [confirmation, setConfirmation] = useState(null)
  const [recentOperation, setRecentOperation] = useState(null)

  const dataCache = useDocumentData()

  const loadTrash = useCallback(

  ({ force = true } = {}) => {
    const url = `/simul/archive/trash?first=0&rows=${MAX_TRASH_ITEMS}`
    setLoading(!dataCache.read(url))
    setError('')
    return dataCache.load('trash', url,

    (data) => {
      const nextItems = Array.isArray(data.items) ? data.items : []
      setItems(nextItems)
      setTotal(Number(data.total || nextItems.length))
      setSelected(current => new Set([...current].filter(key => nextItems.some(item => itemKey(item) === key))))
      setLoading(false)
    }, { force, onClear: () => setItems([]), onError: (error) => {
      setLoading(false)
      setError(errorMessage(error, 'Trash could not be loaded'))
    } }).catch(() => null)
  }, [dataCache])

  useEffect(() => { loadTrash({ force: false }) }, [loadTrash])

  const selectedItems = useMemo(
    () => items.filter(item => selected.has(itemKey(item))),
    [items, selected],
  )
  const allSelected = items.length > 0 && selectedItems.length === items.length
  const visibleSelectionCount = selectedItems.length
  const hasRetainedSelection = selectedItems.some(item => (timestamp(item.retention_expires_at)?.getTime() || 0) > Date.now())
  const hasAnyRetainedItems = items.some(item => (timestamp(item.retention_expires_at)?.getTime() || 0) > Date.now())

  const toggleItem = useCallback(

  item => {
    const key = itemKey(item)
    setSelected(current => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleAll = useCallback(

  () => {
    setSelected(allSelected ? new Set() : new Set(items.map(itemKey)))
  }, [allSelected, items])

  const reportOperation = useCallback(

  (operation, actionLabel) => {
    const nextFailures = operationFailures(operation, items)
    const completed = (operation?.results || []).filter(result => result.status === 'completed').length
    setFailures(nextFailures)
    setNotice(nextFailures.length
      ? `${completed} ${actionLabel}; ${nextFailures.length} ${nextFailures.length === 1 ? 'needs' : 'need'} attention.`
      : `${completed} ${actionLabel}.`)
    setRecentOperation(operation?.undo_expires_at ? operation : null)
  }, [items])

  const restoreSelected = useCallback(

  async () => {
    if (!selectedItems.length) return
    setBusy(true)
    setError('')
    setFailures([])
    try {
      const response = await api.post('/simul/archive/trash/restore', {
        items: selectedItems.map(itemRequest),
      })
      reportOperation(response.data?.operation, 'restored')
      setSelected(new Set())
      await loadTrash({ quiet: true })
      onChanged?.({ action: 'restore', operation: response.data?.operation })
    } catch (requestError) {
      setError(errorMessage(requestError, 'The selected items could not be restored'))
    } finally {
      setBusy(false)
    }
  }, [loadTrash, onChanged, reportOperation, selectedItems])

  const openPermanentConfirmation = useCallback(

  emptying => {
    if (!canPermanentlyDelete || busy) return
    const targets = emptying ? items : selectedItems
    if (!targets.length) return
    setConfirmation({ emptying, items: targets })
  }, [busy, canPermanentlyDelete, items, selectedItems])
  const closePermanentConfirmation = useCallback(

  () => setConfirmation(null), [])

  const permanentlyDelete = useCallback(

  async ({ overrideRetention, reason }) => {
    const targets = confirmation?.items || []
    if (!targets.length) return
    setBusy(true)
    setError('')
    setFailures([])
    try {
      const response = await api.delete('/simul/archive/trash/permanent', { data: {
        confirm: PERMANENT_CONFIRMATION,
        items: targets.map(itemRequest),
        override_retention: overrideRetention,
        ...(reason ? { reason } : {}),
      } })
      reportOperation(response.data?.operation, 'deleted permanently')
      setSelected(new Set())
      setConfirmation(null)
      await loadTrash({ quiet: true })
      onChanged?.({ action: 'permanent_delete', operation: response.data?.operation })
    } catch (requestError) {
      setError(errorMessage(requestError, 'The selected items could not be deleted'))
    } finally {
      setBusy(false)
    }
  }, [confirmation, loadTrash, onChanged, reportOperation])

  const undoRecentOperation = useCallback(

  async () => {
    if (!recentOperation?.operation_id || busy) return
    setBusy(true)
    setError('')
    try {
      await api.post(`/simul/archive/file-operations/${encodeURIComponent(recentOperation.operation_id)}/undo`)
      setNotice('Restore undone. The items are back in trash.')
      setFailures([])
      setRecentOperation(null)
      await loadTrash({ quiet: true })
      onChanged?.({ action: 'undo', operation_id: recentOperation.operation_id })
    } catch (requestError) {
      setError(errorMessage(requestError, 'This operation could not be undone'))
    } finally {
      setBusy(false)
    }
  }, [busy, loadTrash, onChanged, recentOperation])

  return (
    <section className="documents-trash" aria-labelledby="documents-trash-title" aria-busy={loading || busy}>
      <header className="documents-trash-header">
        <div>
          <span className="documents-trash-kicker"><AppIcon icon="pi pi-trash" aria-hidden="true" /> Deleted items</span>
          <h2 id="documents-trash-title">Trash</h2>
          <p>Restore items or leave them here until their retention period ends.</p>
        </div>
        <div className="documents-trash-header-actions">
          {canPermanentlyDelete && items.length > 0 && (
            <button
              type="button"
              className="is-danger-secondary"
              onClick={() => openPermanentConfirmation(true)}
              disabled={busy || total > MAX_TRASH_ITEMS}
              title={total > MAX_TRASH_ITEMS ? `Empty trash supports up to ${MAX_TRASH_ITEMS} items at once` : ''}>
              Empty trash
            </button>
          )}
          {onClose && <button type="button" onClick={onClose} aria-label="Close trash"><AppIcon icon="pi pi-times" /></button>}
        </div>
      </header>

      <div className="documents-trash-announcer" aria-live="polite" aria-atomic="true">
        {notice && (
          <div className="documents-trash-notice">
            <span>{notice}</span>
            {recentOperation && <button type="button" onClick={undoRecentOperation} disabled={busy}>Undo</button>}
          </div>
        )}
        {error && (
          <div className="documents-trash-alert" role="alert">
            <span>{error}</span>
            {!items.length && <button type="button" onClick={() => loadTrash()} disabled={loading}>Try again</button>}
          </div>
        )}
        {failures.length > 0 && (
          <ul className="documents-trash-failures" aria-label="Items needing attention">
            {failures.map(failure => <li key={failure.key}><strong>{failure.name}:</strong> {failure.error}</li>)}
          </ul>
        )}
      </div>

      {loading ? (
        <div className="documents-trash-state" role="status"><span className="documents-trash-spinner" /> Loading trash…</div>
      ) : items.length === 0 && !error ? (
        <div className="documents-trash-state documents-trash-empty">
          <AppIcon icon="pi pi-check-circle" aria-hidden="true" />
          <strong>Trash is empty</strong>
          <span>Deleted files and folders will appear here.</span>
        </div>
      ) : items.length > 0 && (
        <>
          <div className="documents-trash-toolbar" aria-label="Trash actions">
            <label>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={busy} />
              Select all
            </label>
            <span aria-live="polite">{visibleSelectionCount ? `${visibleSelectionCount} selected` : `${total} item${total === 1 ? '' : 's'}`}</span>
            <div>
              <button type="button" onClick={restoreSelected} disabled={busy || !selectedItems.length}>
                <AppIcon icon="pi pi-replay" aria-hidden="true" /> Restore
              </button>
              {canPermanentlyDelete && (
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => openPermanentConfirmation(false)}
                  disabled={busy || !selectedItems.length}>
                  <AppIcon icon="pi pi-trash" aria-hidden="true" /> Delete permanently
                </button>
              )}
            </div>
          </div>

          <div className="documents-trash-list" role="list" aria-label="Deleted files and folders">
            {items.map(item => {
              const key = itemKey(item)
              const selectedItem = selected.has(key)
              return (
                <article className={`documents-trash-item${selectedItem ? ' is-selected' : ''}`} key={key} role="listitem">
                  <label className="documents-trash-select">
                    <input
                      type="checkbox"
                      checked={selectedItem}
                      onChange={() => toggleItem(item)}
                      disabled={busy}
                      aria-label={`Select ${item.name || item.item_id}`} />
                  </label>
                  <span className="documents-trash-item-icon" aria-hidden="true">
                    {item.item_type === 'folder'
                      ? <AppIcon icon="pi pi-folder" />
                      : <DocumentFileTypeIcon file={{ ...item, filename: item.name }} />}
                  </span>
                  <div className="documents-trash-item-main">
                    <strong>{item.name || 'Untitled item'}</strong>
                    <span><AppIcon icon="pi pi-map-marker" aria-hidden="true" /> {originalLocationLabel(item)}</span>
                  </div>
                  <div className="documents-trash-item-facts">
                    <span><b>Deleted</b> {formatTrashDate(item.trashed_at)}</span>
                    <span><b>By</b> {item.trashed_by || 'Unknown user'}</span>
                    <span className={(timestamp(item.retention_expires_at)?.getTime() || 0) <= Date.now() ? 'is-eligible' : ''}>
                      {retentionLabel(item.retention_expires_at)}
                    </span>
                  </div>
                </article>
              )
            })}
          </div>
        </>
      )}

      {confirmation && (
        <TrashConfirmation
          count={confirmation.items.length}
          emptying={confirmation.emptying}
          hasRetainedItems={confirmation.emptying ? hasAnyRetainedItems : hasRetainedSelection}
          busy={busy}
          onCancel={closePermanentConfirmation}
          onConfirm={permanentlyDelete} />
      )}
    </section>
  )
}
